/**
 * Membership resolution helpers.
 *
 * A user may have multiple memberships across tenants and hotels. These helpers
 * resolve the "active" membership from route context (tenant/hotel slug or UUID)
 * and provide utilities for building tenant/hotel switcher UI.
 */

import type { Membership } from '@hostesa/api-client';

export interface MembershipFilter {
  /** Match by tenant slug (from URL path). */
  tenantSlug?: string;
  /** Match by hotel slug (from URL path). */
  hotelSlug?: string;
  /** Match by tenant UUID (preferred over slug — use resolved UUIDs in query keys). */
  tenantId?: string;
  /** Match by hotel UUID. */
  hotelId?: string;
}

/**
 * Resolves the best-matching membership from a list given route context.
 *
 * Resolution priority:
 * 1. Exact UUID match (tenantId + hotelId) — most specific.
 * 2. Slug match (tenantSlug + hotelSlug) — for URL-driven resolution before
 *    UUIDs are known.
 * 3. Tenant-wide membership matching tenantId/tenantSlug with no hotelId
 *    requirement — covers all hotels under the tenant.
 *
 * Returns `null` when no membership covers the requested scope. The caller
 * should treat `null` as a 403-equivalent and redirect to an accessible route.
 *
 * NOTE: Hotel-scoped routes always require both a tenant and hotel identifier.
 * Tenant-scoped routes (audit, usage, members) should pass tenantId/tenantSlug
 * without hotelId/hotelSlug.
 */
export function getActiveMembership(
  memberships: readonly Membership[],
  filter: MembershipFilter,
): Membership | null {
  if (memberships.length === 0) return null;

  // --- 1. Exact UUID match ---
  if (filter.tenantId && filter.hotelId) {
    const exact = memberships.find(
      (m) => m.tenantId === filter.tenantId && m.hotelId === filter.hotelId,
    );
    if (exact) return exact;

    // Tenant-wide membership covers all hotels under the tenant.
    const tenantWide = memberships.find(
      (m) => m.tenantId === filter.tenantId && m.hotelId === null,
    );
    if (tenantWide) return tenantWide;
  }

  // Tenant-level access (no hotelId in filter — e.g. audit, usage, members routes).
  if (filter.tenantId && !filter.hotelId) {
    const tenantWide = memberships.find(
      (m) => m.tenantId === filter.tenantId && m.hotelId === null,
    );
    if (tenantWide) return tenantWide;
  }

  // --- 2. Slug-based match (pre-UUID-resolution, URL-driven) ---
  if (filter.tenantSlug && filter.hotelSlug) {
    const slugExact = memberships.find(
      (m) => m.tenantSlug === filter.tenantSlug && m.hotelSlug === filter.hotelSlug,
    );
    if (slugExact) return slugExact;

    // Tenant-wide membership matching by slug.
    const slugTenantWide = memberships.find(
      (m) => m.tenantSlug === filter.tenantSlug && m.hotelId === null,
    );
    if (slugTenantWide) return slugTenantWide;
  }

  if (filter.tenantSlug && !filter.hotelSlug) {
    const slugTenantWide = memberships.find(
      (m) => m.tenantSlug === filter.tenantSlug && m.hotelId === null,
    );
    if (slugTenantWide) return slugTenantWide;
  }

  return null;
}

/**
 * Returns the unique set of tenants the user has any membership in.
 * Useful for building a tenant switcher.
 */
export function getAccessibleTenants(
  memberships: readonly Membership[],
): Array<{ tenantId: string; tenantSlug?: string; tenantName?: string }> {
  const seen = new Set<string>();
  const tenants: Array<{ tenantId: string; tenantSlug?: string; tenantName?: string }> = [];

  for (const m of memberships) {
    if (!seen.has(m.tenantId)) {
      seen.add(m.tenantId);
      tenants.push({
        tenantId: m.tenantId,
        tenantSlug: m.tenantSlug,
        tenantName: m.tenantName,
      });
    }
  }

  return tenants;
}

/**
 * Returns all hotels the user can access within a given tenant.
 *
 * - If the user has a tenant-wide membership (hotelId: null), no specific hotel
 *   list can be derived from memberships alone — the caller should fetch the
 *   hotel list from the API and present all hotels under the tenant.
 * - If the user only has hotel-scoped memberships, only those specific hotels
 *   are returned.
 *
 * Returns `{ tenantWide: true }` when a tenant-wide grant exists so the caller
 * knows to fetch the full hotel list.
 */
export function getAccessibleHotels(
  memberships: readonly Membership[],
  tenantId: string,
):
  | { tenantWide: true }
  | Array<{ hotelId: string; hotelSlug?: string | null; hotelName?: string | null }> {
  const inTenant = memberships.filter((m) => m.tenantId === tenantId);

  // Tenant-wide membership covers all hotels — caller must fetch the list.
  if (inTenant.some((m) => m.hotelId === null)) {
    return { tenantWide: true };
  }

  const seen = new Set<string>();
  const hotels: Array<{
    hotelId: string;
    hotelSlug?: string | null;
    hotelName?: string | null;
  }> = [];

  for (const m of inTenant) {
    if (m.hotelId && !seen.has(m.hotelId)) {
      seen.add(m.hotelId);
      hotels.push({ hotelId: m.hotelId, hotelSlug: m.hotelSlug, hotelName: m.hotelName });
    }
  }

  return hotels;
}
